import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FormField } from './types';

const TYPE_COLOR: Record<string, string> = {
  text: 'bg-blue-50 text-blue-600',
  number: 'bg-purple-50 text-purple-600',
  select: 'bg-orange-50 text-orange-600',
  multiselect: 'bg-yellow-50 text-yellow-600',
  file: 'bg-slate-100 text-slate-600',
};

interface FieldCardProps {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

export function FieldCard({ field, isSelected, onSelect, onDelete }: FieldCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`group flex w-full items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors text-left ${
        isSelected
          ? 'border-slate-900 bg-slate-50 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-400'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        type="button"
        onClick={(e) => e.stopPropagation()}
        className="mt-0.5 cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing touch-none"
        aria-label="Drag to reorder"
      >
        <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
          <circle cx="3" cy="3" r="1.5" />
          <circle cx="9" cy="3" r="1.5" />
          <circle cx="3" cy="8" r="1.5" />
          <circle cx="9" cy="8" r="1.5" />
          <circle cx="3" cy="13" r="1.5" />
          <circle cx="9" cy="13" r="1.5" />
        </svg>
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="truncate font-medium text-sm text-slate-900">{field.label}</span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLOR[field.type] ?? 'bg-slate-100 text-slate-600'}`}
          >
            {field.type}
          </span>
          {field.required && (
            <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
              Required
            </span>
          )}
        </div>
        {field.helpText && (
          <p className="mt-0.5 text-xs text-slate-400 truncate">{field.helpText}</p>
        )}
        {field.type === 'select' || field.type === 'multiselect' ? (
          <p className="mt-0.5 text-xs text-slate-400">
            {field.options?.length ?? 0} option{field.options?.length !== 1 ? 's' : ''}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="invisible group-hover:visible shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
        aria-label={`Delete field: ${field.label}`}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <path
            d="M1 1l10 10M11 1L1 11"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </button>
  );
}
